import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { vars } from '@/theme';
import { testIds } from '@/test/testIds';
import { SPINNER_SIZE, REGION_TRY_AGAIN_MS, useLoadingZoneRegistration } from '../LoadingZone';
import { regionVeilStyle } from '../LoadingOverlay/LoadingOverlay';
import { Spinner } from '@/components/primitives';
import { RegionTryAgain } from '@/components/feedback/RegionLoadState';
import { useConnectivity } from '@/connectivity/useConnectivity';
import { UnreachableState } from '@/components/ui/UnreachableState';
import { useSpinnerPlacement } from './useSpinnerPlacement';

interface LoadingStateCommon {
  /**
   * Refetch exactly this region's queries. When given, a quiet "Try again"
   * appears 8 px under the spinner 15 s after the region began waiting.
   */
  onRetry?: () => void;
  /** With several regions waiting, this region's spinner wins (else the earliest registered). */
  primary?: boolean;
  /**
   * Centre the spinner on this element instead of the region's visible centre.
   * An element outside the region (several regions sharing one spinner) puts
   * the spinner at that element's viewport centre, unclipped by the region.
   */
  spinnerAnchor?: RefObject<HTMLElement | null>;
}

export type LoadingStateProps = LoadingStateCommon & (
  | {
      /** The layout does not depend on what loads: the caller's skeleton at full strength under a region overlay. */
      shape: 'known';
      children?: ReactNode;
    }
  | {
      /** The layout depends on what loads: the spinner alone, no skeleton. */
      shape: 'unknown';
      children?: never;
    }
);

/** Room an `unknown` region keeps for its spinner when nothing else gives it height. */
const UNKNOWN_MIN_HEIGHT = SPINNER_SIZE + 2 * 32;

/**
 * LoadingState — the one waiting look for a region (#1561, Q-582). No words.
 *
 * - `known`: the caller's skeleton at full strength, a region overlay over
 *   exactly that region (the same veil as `LoadingOverlay`), one spinner.
 * - `unknown`: the spinner alone, centred in the visible part of the region.
 *
 * Registers as a loading zone. With several zones waiting only the leader
 * (`primary`, else one with a retry, else the earliest registered) draws a spinner; its Try again
 * (15 s, `onRetry`) retries every waiting zone.
 *
 * D4 (#239): while the connectivity controller says `unreachable` the leader
 * shows the inline UnreachableState instead of the spinner. A `slow` outage
 * renders nothing of its own: the region keeps waiting (#1561 W4).
 *
 * @example
 * <LoadingState shape="unknown" onRetry={() => void refetch()} />
 *
 * @example
 * <LoadingState shape="known" onRetry={() => void refetch()}>
 *   <ContentCardSkeletonGrid count={18} />
 * </LoadingState>
 */
export function LoadingState({ shape, children, onRetry, primary, spinnerAnchor }: LoadingStateProps) {
  const { isLeader, startedAt, hasRetry, retryAll } = useLoadingZoneRegistration(true, { primary, onRetry });
  const { unreachable, unreachableKind } = useConnectivity();
  const showUnreachable = unreachable && unreachableKind === 'unreachable';
  const known = shape === 'known';

  const rootRef = useRef<HTMLDivElement>(null);
  const showSpinner = isLeader && !showUnreachable;
  const placement = useSpinnerPlacement(rootRef, spinnerAnchor, showSpinner);

  // Try again shows once this waiting episode is REGION_TRY_AGAIN_MS old. The
  // episode start lives in the zone registry, so a remount in the same wait
  // does not restart the clock and Try again's click (which restarts it) hides it.
  const [shownFor, setShownFor] = useState<number | null>(null);
  useEffect(() => {
    if (!showSpinner || !hasRetry || startedAt === null) return;
    const remaining = Math.max(0, startedAt + REGION_TRY_AGAIN_MS - Date.now());
    const t = setTimeout(() => setShownFor(startedAt), remaining);
    return () => clearTimeout(t);
  }, [showSpinner, hasRetry, startedAt]);
  const showTryAgain = showSpinner && hasRetry && startedAt !== null && shownFor === startedAt;

  return (
    <div
      ref={rootRef}
      role="status"
      aria-busy="true"
      data-testid={testIds.ui.loadingState}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: known ? 0 : UNKNOWN_MIN_HEIGHT,
      }}
    >
      {/* Skeleton children — full strength, in normal flow to give the region its natural height */}
      {known && children && (
        <div style={{ overflow: 'hidden', pointerEvents: 'none' }}>
          {children}
        </div>
      )}

      {/* The region overlay: the same veil as LoadingOverlay, over exactly this region */}
      {known && (
        <div data-testid={testIds.ui.loadingOverlay} style={regionVeilStyle} />
      )}

      {/* D4 (#239): unreachable → inline retry state instead of spinner */}
      {isLeader && showUnreachable && (
        <div
          style={{
            ...(known
              ? { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }
              : { flex: 1 }),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 11,
          }}
        >
          <UnreachableState />
        </div>
      )}

      {/* The one spinner: out of flow, so Try again appearing never moves it */}
      {showSpinner && (
        <div
          style={{
            position: placement.fixed ? 'fixed' : 'absolute',
            top: placement.top ?? '50%',
            left: placement.left ?? '50%',
            width: SPINNER_SIZE,
            height: SPINNER_SIZE,
            transform: 'translate(-50%, -50%)',
            zIndex: 11,
          }}
        >
          <Spinner size={SPINNER_SIZE} color={vars.color.success.base} />
          {showTryAgain && (
            <div
              data-testid={testIds.ui.loadingTryAgain}
              style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}
            >
              <RegionTryAgain onRetry={retryAll} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
